import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, brl, effectivePrice } from "@/lib/api";
import { BadgeCheck, CheckCircle2, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import CustomerPicker, { FREE_TEXT } from "@/components/panel/pickers/CustomerPicker";
import ProductPicker, { FREE_TEXT as PRODUCT_FREE_TEXT } from "@/components/panel/pickers/ProductPicker";
import { Badge, Button, Field, Input } from "@/components/ui-kit";
import { dt } from "./SaleDialogs";

/**
 * Diálogos do ORÇAMENTO, extraídos da antiga tela de Orçamentos quando ela e
 * Vendas viraram uma lista só.
 */

const EMPTY_ITEM = { product_id: null, variant_id: null, description: "", qty: 1, unit_price: 0 };
export const EMPTY_QUOTE = { customer_id: null, customer_name: "", phone: "", items: [{ ...EMPTY_ITEM }] };

const METHODS = [["dinheiro", "Dinheiro"], ["pix", "PIX"], ["cartao_debito", "Débito"], ["cartao_credito", "Crédito"]];

const QUOTE_STATUS = {
  enviado: { tom: "warning", rotulo: "Aguardando resposta do cliente" },
  pago: { tom: "success", rotulo: "Pago — sem venda gerada" },
  convertido: { tom: "info", rotulo: "Já virou venda" },
};

/**
 * Detalhe do orçamento — e o que fazer com ele.
 *
 * Clicar na linha do orçamento não abria NADA: a lista tratava venda e venda em
 * aberto com modal e o orçamento sem, na ideia de que os itens já apareciam na
 * linha. Na prática o clique morria, e as duas decisões que importam quando o
 * cliente responde — aprovar ou reabrir para negociar — não tinham onde
 * acontecer.
 *
 * Os dois caminhos são deliberadamente distintos:
 *
 * • APROVAR converte direto, com o preço congelado do orçamento. É o caso em que
 *   o cliente aceitou o que foi enviado — baixa estoque, gera comissão e entra
 *   no caixa de uma vez.
 * • RETOMAR NO PDV traz os itens para o balcão, revalidados contra o catálogo de
 *   agora. É o caso em que ele voltou querendo mudar a quantidade, pedir
 *   desconto ou dividir o pagamento — coisas que só o PDV sabe fazer.
 */
export function QuoteDetail({ quote, onClose, onConvert, onMarkPaid, onResume, pdvPath }) {
  const navigate = useNavigate();
  const [indo, setIndo] = useState(false);
  const st = QUOTE_STATUS[quote.status] || { tom: "neutral", rotulo: quote.status };
  const itens = quote.items || [];
  const aberto = quote.status === "enviado";

  /**
   * Busca o carrinho revalidado ANTES de navegar.
   *
   * Se o orçamento não puder mais virar carrinho (já convertido, produto fora do
   * catálogo), o erro aparece aqui — e não depois da troca de tela, com o
   * vendedor olhando um PDV vazio sem entender o que aconteceu.
   */
  const retomar = async () => {
    setIndo(true);
    try {
      const { data } = await api.get(`/admin/quotes/${quote.id}/cart`);
      onResume?.();
      navigate(pdvPath, { state: { resumeQuote: data } });
    } catch (e) { toast.error(apiError(e)); }
    finally { setIndo(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Orçamento ${quote.number}`} maxWidth="max-w-lg" testId="quote-detail-dialog">
      <div className="space-y-4">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-sm font-semibold text-amber-800">Ainda não é uma venda</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Proposta enviada ao cliente. Nada de estoque, comissão, caixa ou nota fiscal foi
            movimentado — isso acontece ao aprovar ou ao fechar a venda no PDV.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">Cliente</span><p className="text-slate-900">{quote.customer_name || "—"}</p></div>
          <div><span className="text-slate-500">WhatsApp</span><p className="text-slate-900">{quote.contato || quote.phone || "—"}</p></div>
          <div><span className="text-slate-500">Enviado em</span><p className="text-slate-900">{dt(quote.created_at)}</p></div>
          <div><span className="text-slate-500">Situação</span><p className="mt-0.5"><Badge tone={st.tom}>{st.rotulo}</Badge></p></div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Itens</p>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {itens.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">Sem itens.</p>}
            {itens.map((i, k) => (
              <div key={k} className="flex justify-between px-3 py-2 text-sm">
                <span className="text-slate-700">{i.qty}x {i.description}</span>
                <span className="font-mono text-slate-900">{brl((i.unit_price || 0) * (i.qty || 0))}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-3 flex justify-between font-semibold text-slate-900">
          <span>Total orçado</span>
          <span className="font-mono">{brl(quote.total)}</span>
        </div>

        {aberto ? (
          <div className="border-t border-slate-200 pt-4 space-y-2">
            {/* Aprovar é a ação principal — é o desfecho que a loja quer. */}
            <Button icon={CheckCircle2} block size="lg" onClick={onConvert} data-testid="quote-detail-convert-btn">
              Aprovar e gerar venda
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" icon={ShoppingCart} loading={indo} onClick={retomar}
                data-testid="quote-detail-resume-btn">
                Retomar no PDV
              </Button>
              <Button variant="secondary" icon={BadgeCheck} onClick={onMarkPaid} data-testid="quote-detail-paid-btn">
                Marcar pago
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              <b>Aprovar</b> fecha a venda pelo valor orçado. <b>Retomar no PDV</b> abre os itens no balcão
              com preço e estoque de hoje, para mudar quantidade, desconto ou forma de pagamento antes de fechar.
            </p>
          </div>
        ) : (
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs text-slate-500" data-testid="quote-detail-closed">
              {quote.status === "convertido"
                ? "Este orçamento já virou venda — procure a venda correspondente na lista."
                : "Este orçamento foi marcado como pago e não gera venda."}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Confirmação da conversão em venda.
 *
 * O preço fica congelado no orçamento, mas o catálogo pode ter mudado — e o
 * estoque pode não cobrir. Mostrar isso antes evita que o operador descubra com
 * um erro no meio da operação, com o cliente na frente.
 */
export function ConvertDialog({ quote, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [method, setMethod] = useState("dinheiro");
  const [installments, setInstallments] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/quotes/${quote.id}/convert-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(apiError(e)));
  }, [quote.id]);

  const convert = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/quotes/${quote.id}/convert`, {
        payment_method: method,
        installments: method === "cartao_credito" ? parseInt(installments) || 1 : undefined,
      });
      toast.success(`${data.quote_number} virou a venda ${data.sale.number}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const mudou = preview?.itens?.filter((i) => i.mudou) || [];

  return (
    <Modal open onClose={onClose} title={`Converter ${quote.number} em venda`} maxWidth="max-w-lg" testId="quote-convert-dialog">
      {error ? <p className="text-sm text-red-600">{error}</p> : !preview ? <p className="text-sm text-slate-500">Conferindo estoque e preços…</p> : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            A venda baixa o estoque físico, gera comissão para o vendedor do orçamento e entra no caixa,
            igual a uma venda do PDV.
          </p>

          {preview.bloqueios.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5" data-testid="convert-blockers">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-1">Não dá para converter</p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                {preview.bloqueios.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}

          {mudou.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5" data-testid="convert-price-warning">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-1">O preço mudou desde o orçamento</p>
              <ul className="text-sm text-amber-800 space-y-0.5">
                {mudou.map((i, k) => (
                  <li key={k}>{i.description}: orçado {brl(i.preco_orcado)} · hoje {brl(i.preco_atual)}</li>
                ))}
              </ul>
              <p className="text-xs text-amber-700 mt-1.5">
                Será cobrado o valor do orçamento ({brl(preview.total_orcado)}), que foi o aprovado pelo cliente
                {preview.diferenca > 0 ? ` — a diferença de ${brl(preview.diferenca)} entra como desconto.` : "."}
              </p>
            </div>
          )}

          <div className="flex justify-between text-sm border-t border-slate-200 pt-3">
            <span className="text-slate-600">Total a cobrar</span>
            <span className="font-mono font-semibold text-slate-900">{brl(preview.total_orcado)}</span>
          </div>

          <fieldset>
            <legend className="text-eyebrow uppercase tracking-wider text-slate-500">Forma de pagamento</legend>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {METHODS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`convert-method-${v}`}
                  aria-pressed={method === v}
                  className={`text-sm font-semibold px-3 h-9 rounded-lg border ${method === v ? "bg-brand-600 border-brand-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
            {method === "cartao_credito" && (
              <Input type="number" min="1" max="24" value={installments} onChange={(e) => setInstallments(e.target.value)}
                data-testid="convert-installments" placeholder="Parcelas" aria-label="Número de parcelas"
                mono className="mt-2 w-28" />
            )}
          </fieldset>

          <Button onClick={convert} loading={saving} disabled={!preview.pode_converter} size="lg" block data-testid="convert-confirm">
            {saving ? "Gerando venda..." : `Confirmar venda de ${brl(preview.total_orcado)}`}
          </Button>
        </div>
      )}
    </Modal>
  );
}

/** Cadastro de orçamento fora do PDV — cliente que liga pedindo preço. */
export function NewQuoteModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ ...EMPTY_QUOTE });
  const [busy, setBusy] = useState(false);

  const setItem = (i, k, v) => setItems(i, { [k]: v });
  /** Atualiza vários campos de um item de uma vez (o picker preenche 4 juntos). */
  const setItems = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  const formTotal = form.items.reduce((s, i) => s + (parseInt(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);

  const create = async () => {
    if (!form.customer_name.trim()) { toast.error("Informe o cliente"); return; }
    if (!form.phone.trim()) { toast.error("Informe o WhatsApp para enviar o orçamento"); return; }
    if (form.items.some((i) => !i.description.trim())) { toast.error("Descreva todos os itens"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/quotes", {
        ...form,
        items: form.items.map((i) => ({ ...i, qty: parseInt(i.qty), unit_price: parseFloat(i.unit_price) })),
      });
      toast.success(`Orçamento ${data.number} criado`);
      setForm({ ...EMPTY_QUOTE, items: [{ ...EMPTY_ITEM }] });
      onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Novo orçamento" maxWidth="max-w-lg" testId="quote-form-dialog">
      <div className="space-y-3">
        <Field label="Cliente" required>
          <CustomerPicker
            value={form.customer_id || (form.customer_name ? FREE_TEXT : "")}
            selectedLabel={form.customer_name}
            onChange={(c) => setForm({
              ...form,
              customer_id: c?.id || null,
              customer_name: c?.name || "",
              // Telefone do cadastro entra sozinho: é por ele que o orçamento
              // vai para o WhatsApp.
              phone: c?.phone || form.phone,
            })}
            placeholder="Busque um cliente cadastrado ou digite o nome"
            allowFreeText
            testId="quote-form-customer"
          />
        </Field>
        <Field label="WhatsApp" required hint="É por aqui que o orçamento é enviado.">
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="5511999999999" data-testid="quote-form-phone" />
        </Field>

        <fieldset className="space-y-2">
          <legend className="text-eyebrow uppercase tracking-wider text-slate-500">Itens</legend>
          {form.items.map((it, i) => (
            <div key={i} className="border border-slate-100 rounded-lg p-2 space-y-2">
              {/* Um campo só: item do catálogo preenche descrição e preço e
                  grava o vínculo (o que permite converter em venda depois);
                  item fora do catálogo vale pelo texto digitado. */}
              <ProductPicker
                productId={it.product_id || (it.description ? PRODUCT_FREE_TEXT : "")}
                variantId={it.variant_id || ""}
                selectedLabel={it.description}
                onChange={({ product, variant, freeText }) => setItems(i, {
                  product_id: product?.id || null,
                  variant_id: variant?.id || null,
                  description: product
                    ? `${product.name}${variant && variant.label !== "Único" ? ` (${variant.label})` : ""}`
                    : (freeText ?? ""),
                  unit_price: product ? effectivePrice(product, variant) : it.unit_price,
                })}
                placeholder="Busque no catálogo ou digite o item"
                allowFreeText
                testId={`quote-item-desc-${i}`}
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 shrink-0">Qtd</span>
                <Input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)}
                  aria-label={`Quantidade do item ${i + 1}`} data-testid={`quote-item-qty-${i}`} size="sm" mono className="w-20 px-2" />
                <span className="text-xs text-slate-400 shrink-0">Valor unit. R$</span>
                <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)}
                  aria-label={`Valor unitário do item ${i + 1}`} data-testid={`quote-item-price-${i}`} size="sm" mono className="flex-1 px-2" />
                <Button variant="ghost" size="iconSm" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })}
                  aria-label={`Remover item ${i + 1}`} className="hover:text-red-600 shrink-0"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
          <Button variant="link" size="sm" icon={Plus} onClick={() => setForm({ ...form, items: [...form.items, { ...EMPTY_ITEM }] })}
            data-testid="quote-add-item">Adicionar item</Button>
        </fieldset>

        <p className="text-sm text-right text-slate-600">Total: <span className="font-mono font-semibold text-slate-900">{brl(formTotal)}</span></p>
        <Button onClick={create} loading={busy} size="lg" block data-testid="quote-form-save">
          {busy ? "Gerando..." : "Gerar orçamento + link"}
        </Button>
      </div>
    </Modal>
  );
}
